import { useMemo, useState } from "react";
import { Button, DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listInventory } from "../../api/inventoryApi";
import type { InventoryItemOutExt } from "../../api/types";
import { useAsyncData } from "../../lib/useAsyncData";
import { StatusBanner } from "../../components/StatusBanner";
import { BranchSelect } from "../../components/BranchSelect";
import { BulkLabelPrintModal, type LabelEntry } from "../../components/BulkLabelPrintModal";

type InventoryItemOut = InventoryItemOutExt;
type Selection = Record<string, { selected: boolean; copies: number }>;

/**
 * Bulk label printing (business-owner request: label many products in one batch instead of
 * one at a time from a product's detail page — see `LabelPrintModal.tsx`). Selection state
 * is keyed by item id at this page level (not derived from the filtered/paginated view
 * below it), so searching or switching branch never drops products already selected.
 */
export function LabelsPage() {
  const { user, ensureFreshToken } = useAuth();
  const [branchId, setBranchId] = useState(user?.assigned_branch_id ?? "");

  const itemsState = useAsyncData(
    () => ensureFreshToken().then((token) => listInventory(token, branchId || undefined)),
    [branchId],
  );
  const items = itemsState.data ?? [];
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const [searchQuery, setSearchQuery] = useState("");
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query.length === 0) return items;
    return items.filter(
      (item) => item.name.toLowerCase().includes(query) || item.sku.toLowerCase().includes(query),
    );
  }, [items, searchQuery]);

  const [selection, setSelection] = useState<Selection>({});
  const selectedCount = Object.values(selection).filter((s) => s.selected).length;
  const totalCopies = Object.values(selection).reduce((sum, s) => (s.selected ? sum + s.copies : sum), 0);

  function toggleSelected(id: string) {
    setSelection((prev) => ({
      ...prev,
      [id]: { selected: !prev[id]?.selected, copies: prev[id]?.copies ?? 1 },
    }));
  }

  function setCopies(id: string, copies: number) {
    setSelection((prev) => ({ ...prev, [id]: { selected: prev[id]?.selected ?? false, copies } }));
  }

  const [columnsPerPage, setColumnsPerPage] = useState(3);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [includePrice, setIncludePrice] = useState(false);
  const [printLabels, setPrintLabels] = useState<LabelEntry[] | null>(null);

  function handlePrint() {
    const labels: LabelEntry[] = [];
    for (const [id, sel] of Object.entries(selection)) {
      if (!sel.selected) continue;
      const item = itemsById.get(id);
      if (!item) continue;
      for (let i = 0; i < sel.copies; i++) {
        labels.push({ itemId: id, name: item.name, sku: item.sku, priceLabel: item.retail_price });
      }
    }
    setPrintLabels(labels);
  }

  const columns: DataTableColumn<InventoryItemOut, keyof InventoryItemOut>[] = [
    {
      key: "id",
      header: "",
      render: (r) => (
        <input
          type="checkbox"
          checked={!!selection[r.id]?.selected}
          onChange={() => toggleSelected(r.id)}
          aria-label={`Select ${r.name} for labels`}
        />
      ),
    },
    { key: "sku", header: "SKU", render: (r) => r.sku },
    { key: "name", header: "Name", render: (r) => r.name },
    { key: "retail_price", header: "Retail Price", align: "right", render: (r) => r.retail_price },
    {
      key: "current_stock",
      header: "Copies",
      align: "right",
      render: (r) => (
        <input
          type="number"
          className="sl-input"
          style={{ width: 64 }}
          min={1}
          disabled={!selection[r.id]?.selected}
          value={selection[r.id]?.copies ?? 1}
          onChange={(e) => setCopies(r.id, Math.max(1, Number(e.target.value) || 1))}
          aria-label={`Number of labels to print for ${r.name}`}
        />
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Labels</h1>
          <p className="page__subtitle">
            Select products, set how many labels each needs, and print them as one sheet — useful for
            labeling multiple items at once.
          </p>
        </div>
      </div>

      {itemsState.error ? <StatusBanner kind="error">{itemsState.error}</StatusBanner> : null}

      <div className="panel">
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="labels-search">
              Search
            </label>
            <input
              id="labels-search"
              type="search"
              className="sl-input"
              placeholder="Search by name or SKU…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <BranchSelect id="labels-branch-filter" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
        </div>

        {itemsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={filteredItems}
            getRowId={(row) => row.id}
            emptyMessage="No inventory items found."
            caption="Select products for label printing"
          />
        )}
      </div>

      <div className="panel">
        <div className="form-grid">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="labels-columns">
              Columns per page
            </label>
            <input
              id="labels-columns"
              type="number"
              className="sl-input"
              min={1}
              value={columnsPerPage}
              onChange={(e) => setColumnsPerPage(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="labels-rows">
              Rows per page
            </label>
            <input
              id="labels-rows"
              type="number"
              className="sl-input"
              min={1}
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
          <label className="sl-field">
            <span className="sl-field__label">Include price on label</span>
            <input type="checkbox" checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} />
            <span className="sl-field__hint">
              Off by default — printing price means a future price change forces relabeling stock already on
              the shelf.
            </span>
          </label>
        </div>
        <div className="form-actions">
          <p className="muted" style={{ marginRight: "auto" }}>
            {selectedCount} product(s) selected, {totalCopies} label(s) total.
          </p>
          <Button type="button" disabled={totalCopies === 0} onClick={handlePrint}>
            Print labels
          </Button>
        </div>
      </div>

      <BulkLabelPrintModal
        open={printLabels !== null}
        onClose={() => setPrintLabels(null)}
        labels={printLabels ?? []}
        columnsPerPage={columnsPerPage}
        rowsPerPage={rowsPerPage}
        includePrice={includePrice}
      />
    </div>
  );
}
